import { TnEngineExtraContext } from 'pytha';
import { DependencyList, EffectCallback, useContext, useEffect, useRef } from 'react';
import {
    createPrimitive,
    createPrimitiveMulti,
    deletePrimitivesInGraphic,
    findPrimitivesInGraphic,
} from "@/api/geometry/primitive";
import { findFontGlyphs, findFontTtfs, findFontsInGraphic } from "@/api/geometry/font";
import { deleteUcs, saveUcs } from "@/api/geometry/ucs";
import { deleteLayer, saveLayer, updateLayer } from "@/api/geometry/layer";
import { addRoadNetworkToUndo, deleteRoadNetworkByUuids, getRoadNetworkByUuids, updateRoadNetwork } from "@/api/building/road-network";
import { SystemStatus } from '@/commons/context/system-store';
import { RoadNetwork } from '@/commons/interface/building/road-network';
import { StoreyStatus } from '@/commons/context/storey-store';
import { addGroup, deleteGroup } from '@/api/spec-common/group';

/**
 * @description: Hooks useCmdRegisterEffect 注册cmd
 * @demo useCmdRegisterEffect(systemContext, [systemContext.currentBranchUuid, systemContext.currentModelUuid, systemContext.currentProjectUuid]);
 */

export const useCmdRegisterEffect = (systemContext: SystemStatus, storeyContext: StoreyStatus, deps: DependencyList) => {

    useEffect(() => {
        let extraContext = TnEngineExtraContext.getInstance();
        extraContext.apiContext.createPrimitive = (data, params) => createPrimitive(data, {
            ...params,
            branchUuid: systemContext.currentBranchUuid,
            modelUuid: systemContext.currentModelUuid,
            projectUuid: systemContext.currentProjectUuid,
            storeyUuid: storeyContext?.currentStorey?.uuid,
        });
        extraContext.apiContext.createPrimitiveMulti = (data: any, params) => createPrimitiveMulti(data, {
            ...params,
            branchUuid: systemContext.currentBranchUuid,
            modelUuid: systemContext.currentModelUuid,
            projectUuid: systemContext.currentProjectUuid,
            storeyUuid: storeyContext?.currentStorey?.uuid,
        });
        extraContext.apiContext.deletePrimitivesInGraphic = (uuids: string[], params) => deletePrimitivesInGraphic(uuids, systemContext.currentProjectUuid, {
            ...params
        });
        extraContext.apiContext.findPrimitivesInGraphic = (graphicId: string, params) => findPrimitivesInGraphic(systemContext.currentProjectUuid, { ...params });
        extraContext.apiContext.findOneTypePrimitivesInGraphic = (graphicId: string, type: string, params) => findPrimitivesInGraphic(systemContext.currentProjectUuid, { ...params, type: type });
        extraContext.apiContext.saveUcs = (data: any, params) => saveUcs({ ...data, projectUuid: systemContext.currentProjectUuid },
            systemContext.currentProjectUuid, systemContext.currentModelUuid, systemContext.currentBranchUuid,
            { ...params });
        extraContext.apiContext.deleteUcs = (uuid: string, params) => deleteUcs(uuid, { ...params });
        extraContext.apiContext.updateLayer = (data: any, params) => updateLayer(data, systemContext.currentProjectUuid, { ...params });
        extraContext.apiContext.createLayer = (data: any, params) => saveLayer(data, systemContext.currentProjectUuid, systemContext.currentModelUuid, systemContext.currentBranchUuid, { ...params });;
        extraContext.apiContext.deleteLayer = (uuid: string, params) => deleteLayer(uuid, { ...params });
        extraContext.apiContext.findFontGlyphs = (params) => findFontGlyphs({ ...params });
        extraContext.apiContext.findFontsInGraphic = (graphicId: string, params) => findFontsInGraphic(systemContext.currentProjectUuid, systemContext.currentModelUuid, systemContext.currentBranchUuid, { ...params });
        extraContext.apiContext.findFontTtfs = (params) => findFontTtfs({ ...params });
        extraContext.apiContext.registerApi("updateRoadNetwork", (uuid: string, data: RoadNetwork, params) => updateRoadNetwork(uuid, data, { ...params }));
        extraContext.apiContext.registerApi("getRoadNetworkByUuids", (uuids: string[], params) => getRoadNetworkByUuids(uuids, { ...params }));
        extraContext.apiContext.registerApi("deleteRoadNetworkByUuids", (uuids: string[], params) => deleteRoadNetworkByUuids(uuids, { ...params }));
        extraContext.apiContext.registerApi("addRoadNetworkToUndo", (data: RoadNetwork[], params) => addRoadNetworkToUndo(data, { ...params }));

        extraContext.apiContext.addGroup = (data: any, groupUuid: string, hasEngine: boolean, params) => addGroup(systemContext.currentProjectUuid, systemContext.currentModelUuid, data, groupUuid, hasEngine, { ...params });
        extraContext.apiContext.deleteGroup = (groupUuid: string, params) => deleteGroup(groupUuid, { ...params });
    }, deps);
};
